<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

import AtendeTotalDatetimeRangePicker from './DatetimeRangePicker.vue';
import StandbiesAPI from 'dashboard/api/atendeTotal/standbies';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

export default {
  components: {
    AtendeTotalDatetimeRangePicker,
    NextButton,
    TagMultiSelectComboBox,
  },
  props: {
    onClose: {
      type: Function,
      required: true,
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      dateRange: [new Date(), new Date()],
      dateRangeError: null,
      show: true,
      selectedAgents: [],
    };
  },
  validations() {
    return {
      selectedAgents: {
        required,
        isEmpty: value => !!value.length,
      },
    };
  },
  computed: {
    ...mapGetters({ getTeamMembers: 'teamMembers/getTeamMembers' }),
    teamId() {
      return this.$route.params.teamId;
    },
    teamMembers() {
      return this.getTeamMembers(this.teamId);
    },
    agentOptions() {
      return this.teamMembers.map(({ id, name }) => ({
        value: id,
        label: name,
      }));
    },
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    onDateRangeChange(value) {
      this.dateRange = value;
      this.dateRangeError = null;
    },
    async loadData() {
      await this.$store.dispatch('agents/get');
      await this.$store.dispatch('teamMembers/get', { teamId: this.teamId });
      await this.$store.dispatch('standbies/get', this.teamId);
    },
    async createStandbyTeamDistribution() {
      const [start_at, end_at] = this.dateRange;

      try {
        await StandbiesAPI.create(this.teamId, {
          start_at,
          end_at,
          agents_ids: this.selectedAgents,
        });

        await this.loadData();

        this.onClose();
      } catch (error) {
        const timeRangeErrorMessage = error.response?.data?.time_range?.[0];

        if (timeRangeErrorMessage) {
          if (
            timeRangeErrorMessage ===
            'there already standby lead distribution for this time'
          ) {
            this.dateRangeError =
              'Já existe um plantão para este intervalo de tempo.';
          } else {
            this.dateRangeError =
              'Um erro inesperado ocorreu. Tente novamente.';
          }
        } else {
          this.dateRangeError = 'Um erro inesperado ocorreu. Tente novamente.';
        }

        throw error;
      }
    },
  },
};
</script>

<template>
  <woot-modal v-model:show="show" :on-close="onClose">
    <div class="h-auto flex flex-col">
      <woot-modal-header header-title="Novo plantão" />

      <!-- The agent dropdown is absolute inside the modal's overflow-auto box: reserve room for it. -->
      <form
        class="flex flex-col w-full items-start min-h-[28rem]"
        @submit.prevent="createStandbyTeamDistribution()"
      >
        <div class="w-full">
          <label :class="{ error: dateRangeError }">
            {{ $t('TEAMS_SETTINGS.STANDBY.PERIOD') }}
            <AtendeTotalDatetimeRangePicker
              show-range
              class="no-margin auto-width"
              :value="dateRange"
              confirm-text="Aplicar"
              placeholder="Selecionar intervalo de datas"
              :error="dateRangeError"
              @change="onDateRangeChange"
            />
          </label>
        </div>
        <div class="w-full pt-2">
          <label :class="{ error: v$.selectedAgents.$error }">
            {{ $t('TEAMS_SETTINGS.STANDBY.AGENTS') }}
            <TagMultiSelectComboBox
              v-model="selectedAgents"
              :options="agentOptions"
              :has-error="v$.selectedAgents.$error"
              placeholder="Escolha agentes do time para o plantão"
              class="mt-1"
              @update:model-value="v$.selectedAgents.$touch"
            />
            <span v-if="v$.selectedAgents.$error" class="message">
              {{ $t('INBOX_MGMT.ADD.AGENTS.VALIDATION_ERROR') }}
            </span>
          </label>
        </div>
        <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full mt-auto">
          <NextButton
            type="submit"
            :disabled="v$.selectedAgents.$invalid"
            label="Criar novo plantão"
          />
          <button class="button clear" @click.prevent="onClose">
            {{ $t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT') }}
          </button>
        </div>
      </form>
    </div>
  </woot-modal>
</template>
